import { JSX, ReactNode, MouseEvent } from "react"
import { cn } from "../../../lib/utils"
import { ChevronDown } from "lucide-react"

export interface TabItem {
    readonly id: string
    readonly header: string
    readonly subheader?: string
    readonly children?: ReadonlyArray<TabItem>
    readonly actionIcon?: ReactNode
    readonly isSelectable?: boolean
}

export interface VerticalNavPanelProps {
    title: string
    titleClassName?: string
    headerClassName?: string
    headerAction?: ReactNode
    items?: ReadonlyArray<TabItem>

    // Selection
    selectedId?: string
    onSelect?: (id: string) => void

    // Expansion
    expandedIds?: ReadonlyArray<string>
    onToggle?: (id: string) => void

    className?: string
    rowClassName?: string 
    showTitleOnHover?: boolean
    listContainerClassName?: string
    rowHeaderClassName?: string 
}

export interface TabRowProps {
    item: TabItem
    isSelected: boolean
    isExpanded: boolean
    onClick: (item: TabItem) => void
    onToggle: (item: TabItem) => void
    className?: string
    showTitleOnHover?: boolean
    rowHeaderClassName?: string
}

export function TabRow ({
    item,
    isSelected,
    isExpanded,
    onClick,
    onToggle,
    className,
    showTitleOnHover,
    rowHeaderClassName
}: TabRowProps) : JSX.Element {
    const isSelectable = item.isSelectable ?? true;
    const hasChildren = item.children && item.children.length > 0;

    const handleMainClick = () => {
      if (isSelectable) {
        onClick(item)
      } else {
        onToggle(item)
      }
    }

    const handleChevronClick = (e: MouseEvent) => {
      e.stopPropagation(); // Stops the click from bubbling to handleMainClick
      onToggle(item)
    }

    return (
        <div 
            onClick={handleMainClick}
            className={cn(
                "flex items-center w-full py-2 px-3 text-left rounded-lg transition-colors border-none outline-none",
                isSelectable ? "cursor-pointer" : "cursor-default",
                isSelectable && isSelected
                    ? "bg-[#E2E8F0] text-[#0A0A0A]"
                    : "bg-transparent text-[#737373]",
                isSelectable && !isSelected && "hover:bg-[#E2E8F0]",
                !isSelectable && "font-semibold hover:bg-transparent",
                className
            )}
        >
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <div className={cn( 
                  "text-sm font-normal leading-none truncate",
                  isSelected ? "text-[#0A0A0A]" : "text-[#737373]",
                  rowHeaderClassName 
                )}
                title={showTitleOnHover ? item.header : undefined}
              >
                {item.header}
              </div>
              {item.subheader && (
                  <div className="text-xs text-[#737373] font-normal leading-none truncate">
                      {item.subheader}
                  </div>
              )}
            </div>

            {hasChildren && (
              <div
                role="button"
                onClick={handleChevronClick}
                className={cn(
                  "p-0.5 rounded-md transition-all flex items-center justify-center z-10",
                  "hover:bg-[#CBD5E1] hover:scale-110",
                  isExpanded ? "rotate-0" : "-rotate-90",
                  isSelected || isExpanded ? "text-[#0A0A0A]" : "text-[#737373]"
                )}
              >
                <ChevronDown className="w-4 h-4" />
              </div>
            )}
        </div>
    )
}

export function VerticalNavPanel({
  title,
  titleClassName,
  headerClassName,
  headerAction,
  items = [],
  selectedId,
  onSelect,
  expandedIds = [],
  onToggle,
  className,
  rowClassName,
  showTitleOnHover,
  listContainerClassName,
  rowHeaderClassName,
}: VerticalNavPanelProps) {
  const itemsExist = items.length > 0;

  return (
    <div className={cn("bg-slate-100 w-[280px] flex flex-col rounded-lg", className)}>
      {title &&
        <div className={cn("flex justify-between items-center w-full p-3 border-b border-slate-100 mb-1", headerClassName)}>
          <span className={cn("text-[#737373]", titleClassName)}>{title}</span>
          <div>{headerAction}</div>
        </div>
      }

      <div className={cn("flex flex-col gap-2 pb-[10px]", listContainerClassName)}>
        {itemsExist &&
          items.map((item) => {
            const isExpanded = expandedIds.includes(item.id);
            
            return (
              <div key={item.id} className="flex flex-col">
                <TabRow
                  item={item}
                  isSelected={item.id === selectedId}
                  isExpanded={isExpanded}
                  onClick={(i) => onSelect?.(i.id)}
                  onToggle={(i) => onToggle?.(i.id)}
                  className={cn("rounded-[8px]", rowClassName)}
                  showTitleOnHover={showTitleOnHover}
                  rowHeaderClassName={rowHeaderClassName}
                />

                {isExpanded && item.children && item.children.length > 0 && (
                  <div className="relative flex flex-col gap-1 pt-[2px] pb-[2px] pl-6">
                    <div className="absolute left-4 top-0 bottom-0 w-px bg-[#E5E5E5]" />
                    
                    {item.children.map((child) => (
                      <TabRow
                        key={child.id}
                        item={child}
                        isSelected={child.id === selectedId}
                        isExpanded={false} 
                        onClick={(i) => onSelect?.(i.id)}
                        onToggle={(i) => onToggle?.(i.id)}
                        className={cn("rounded-[8px] h-[28px]", rowClassName)}
                        showTitleOnHover={showTitleOnHover}
                        rowHeaderClassName={rowHeaderClassName} 
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}